import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { JobStatusBadge } from './JobStatusBadge';
import { Job } from '@recruitai/shared';
import { useNavigate } from 'react-router-dom';
import { useCloneJob } from '@/hooks/useCloneJob';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Copy, Loader2 } from 'lucide-react';

interface JobCardProps {
  job: Job;
}

export function JobCard({ job }: JobCardProps) {
  const navigate = useNavigate();
  const { cloneJob, isCloning } = useCloneJob();
  const { user } = useAuth();
  const canClone = user?.role === 'recruiter' || user?.role === 'admin' || user?.role === 'owner';

  const handleClone = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const cloned = await cloneJob(job.jobId);
      toast.success('Vaga clonada com sucesso');
      navigate(`/jobs/${cloned.jobId}/edit`);
    } catch (err: any) {
      if (err.code === 'QUOTA_EXCEEDED') {
        toast.warning('Limite de vagas do plano atingido. Faça upgrade para continuar.');
      } else if (err.code === 'FORBIDDEN') {
        toast.error('Você não tem permissão para clonar vagas.');
      } else {
        toast.error('Erro ao clonar vaga. Tente novamente.');
      }
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/jobs/${job.jobId}`)}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <CardTitle className="text-xl">{job.title}</CardTitle>
            <CardDescription className="mt-1">
              {job.candidateCount} candidatos
              {job.expiresAt && (
                <span className="ml-2">
                  • Expira em {new Date(job.expiresAt).toLocaleDateString('pt-BR')}
                </span>
              )}
            </CardDescription>
          </div>
          <JobStatusBadge status={job.status} />
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {job.description}
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/jobs/${job.jobId}/edit`);
            }}
          >
            Editar
          </Button>
          {canClone && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClone}
              disabled={isCloning}
            >
              {isCloning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4 mr-1" />}
              {!isCloning && 'Clonar'}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/jobs/${job.jobId}`);
            }}
          >
            Ver Detalhes
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
